import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { themeConWeb } from '@app/util/modernThemeConweb'
import {
  Box,
  FilledInput,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Typography,
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { Form, Formik } from 'formik'
import { isEmpty } from 'lodash'

import scriptLoader from 'react-async-script-loader'
import MaskedInput from 'react-text-mask'
import styled from 'styled-components'
import { object, string } from 'yup'

import { api, FOODSBY_BASE_URL } from '../../api/api'
import { loadCreditCardsStart } from '../../redux/modules/checkout'
import { selectAllCreditCards, selectCreditCardsIsLoading } from '../../redux/modules/selectors'
import { createSecureToken } from '../../services/heartland'
import { HEARTLAND_SCRIPT_URL } from '../../services/heartland'
import { creditCardMask, cvvMask, expDateMask, zipCodeMask } from '../../util/inputMasks'
import { validateCcExpiration } from '../../util/validators'

const validationSchema = object().shape({
  cvv: string()
    .required('CVV is required.')
    .min(3, 'Enter a valid CVV.'),
  expirationDate: string()
    .required('Expiration date is required.')
    .test('test-cc-exp-date', 'Enter a valid expiration date.', validateCcExpiration),
  name: string().required('Name on card is required.'),
  number: string()
    .required('Card number is required.')
    .min(18, 'Enter a valid card number.'),
  zipCode: string()
    .required('Zip code is required.')
    .min(5, 'Enter a valid zip code.'),
})

const CustomMaskedInput = props => {
  const { inputRef, ...others } = props
  return <MaskedInput {...others} guide={false} />
}

// This component is exported separately to provide a button that looks like a credit card radio list item (RadioPaymentMethod)
// It is used in PanelSubscriptions
export const ButtonPaymentMethod = ({ active, children, disabled, onClick }) => (
  <RootButtonPaymentMethod active={active} disabled={disabled} onClick={onClick}>
    <img alt="" src="https://images.foodsby.com/conweb/creditcard-icon.png" />
    &nbsp;&nbsp;
    {children}
  </RootButtonPaymentMethod>
)

const RadioPaymentMethod = ({ onChangePayment, selected, text, value }) => (
  <RootRadioPaymentMethod onClick={() => onChangePayment(value)} selected={selected}>
    <InputRadioPaymentMethod
      checked={selected}
      name="paymentMethod"
      onChange={() => onChangePayment(value)}
      type="radio"
      value={value}
    />
    <img
      alt=""
      className="-creditCardIcon"
      src="https://images.foodsby.com/conweb/creditcard-icon.png"
    />
    <Typography className="-text">{text}</Typography>
  </RootRadioPaymentMethod>
)

const FormCreditCardDetails = ({ formRef, handleSubmit }) => (
  <Formik
    enableReinitialize
    initialValues={{
      cvv: '',
      expirationDate: '',
      name: '',
      number: '',
      // saveCard is not currently used but we will need to add a checkbox for it once or if this component is used in checkout.
      saveCard: false,
      zipCode: '',
    }}
    innerRef={formRef}
    // Trigger submission with handleSubmit using the formRef (see PanelSubscriptions for example)
    onSubmit={values => handleSubmit(values)}
    validationSchema={validationSchema}
  >
    {({ errors, handleBlur, handleChange, touched }) => (
      <FormContainer>
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth margin="normal" size="small" variant="filled">
              <InputLabel htmlFor="number">Card Number</InputLabel>
              <FilledInput
                id="number"
                inputComponent={CustomMaskedInput}
                inputProps={{ mask: creditCardMask }}
                label="Card Number"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="1234  5678  9101  1123"
              />
              <FormHelperText error>{touched?.number && errors?.number}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth margin="normal" size="small" variant="filled">
              <InputLabel htmlFor="name">Name On Card</InputLabel>
              <FilledInput
                id="name"
                label="Name on Card"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="Jane Dough"
              />
              <FormHelperText error>{touched?.name && errors?.name}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item sm={4} xs={12}>
            <FormControl fullWidth margin="normal" size="small" variant="filled">
              <InputLabel htmlFor="expirationDate">Expiration Date</InputLabel>
              <FilledInput
                id="expirationDate"
                inputComponent={CustomMaskedInput}
                inputProps={{ mask: expDateMask }}
                label="Expiration Date"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="01/20"
              />
              <FormHelperText error>
                {touched?.expirationDate && errors?.expirationDate}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={4} xs={12}>
            <FormControl fullWidth margin="normal" size="small" variant="filled">
              <InputLabel htmlFor="cvv">CVV</InputLabel>
              <FilledInput
                id="cvv"
                inputComponent={CustomMaskedInput}
                inputProps={{ mask: cvvMask }}
                label="CVV"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="123"
              />
              <FormHelperText error>{touched?.cvv && errors?.cvv}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sm={4} xs={12}>
            <FormControl fullWidth margin="normal" size="small" variant="filled">
              <InputLabel htmlFor="zipCode">Zip Code</InputLabel>
              <FilledInput
                id="zipCode"
                inputComponent={CustomMaskedInput}
                inputProps={{ mask: zipCodeMask }}
                label="Zip Code"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="12345"
              />
              <FormHelperText error>{touched?.zipCode && errors?.zipCode}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </FormContainer>
    )}
  </Formik>
)

const PaymentMethod = ({
  ccFormRef,
  ccProfileId,
  onError,
  onSuccess,
  setCcProfileId,
  setSubmitting,
  onlyExistingCards,
}) => {
  const dispatchRedux = useDispatch()
  const creditCards = useSelector(selectAllCreditCards)
  const loading = useSelector(selectCreditCardsIsLoading)

  // Effects
  useEffect(() => {
    if (creditCards.length === 0) {
      dispatchRedux(loadCreditCardsStart())
    }
  }, [dispatchRedux, creditCards.length])

  useEffect(() => {
    if (!isEmpty(creditCards) && !ccProfileId) {
      setCcProfileId(creditCards[0].ccProfileId)
    }
    //eslint-disable-next-line
  }, [creditCards, setCcProfileId])

  useEffect(() => {
    if (ccProfileId) {
      onError(null)
    }
  }, [ccProfileId, onError])

  const submitCreditCardDetail = async values => {
    try {
      setSubmitting(true)
      const [expMonth, expYear] = values.expirationDate.split('/')
      let tokenRequest = {
        expMonth,
        expYear: '20' + expYear,
        number: values.number.trim(),
        ...values,
      }
      const res = await createSecureToken(tokenRequest)
      const ccProfile = {
        cardholderName: values.name,
        expirationMonth: res.expMonth,
        expirationYear: res.expYear,
        lastFour: res.lastFour,
        singleUseToken: res.token,
        zip: res.zipCode,
      }

      try {
        const res = await api.post(`${FOODSBY_BASE_URL}/api/v1/salad/web/wallet/payment`, ccProfile)
        onSuccess(res.id)
      } catch (err) {
        onError(err.message)
      }
    } catch (err) {
      onError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return loading ? (
    <SkeletonPaymentMethod />
  ) : (
    <>
      {creditCards?.map(card => (
        <RadioPaymentMethod
          key={card.ccProfileId}
          onChangePayment={setCcProfileId}
          selected={card.ccProfileId === ccProfileId}
          text={<span>&bull;&bull;&bull;&bull;&nbsp;&nbsp;{card.lastFour}</span>}
          value={card.ccProfileId}
        />
      ))}
      {!onlyExistingCards && (
        <>
          <ButtonPaymentMethod active={!ccProfileId} onClick={() => setCcProfileId(null)}>
            <Typography color="secondary" variant="body2">
              + Add new card
            </Typography>
          </ButtonPaymentMethod>
          {(isEmpty(creditCards) || (!isEmpty(creditCards) && !ccProfileId)) && (
            <FormCreditCardDetails formRef={ccFormRef} handleSubmit={submitCreditCardDetail} />
          )}
        </>
      )}
    </>
  )
}

const SkeletonPaymentMethod = () => (
  <Box marginBottom={2}>
    <Skeleton height={80} />
    <Skeleton height={80} />
  </Box>
)

const RootRadioPaymentMethod = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: calc(${themeConWeb.baseUnit} * 4);
  padding: calc(${themeConWeb.baseUnit} * 4);
  border-radius: ${themeConWeb.baseUnit};
  box-shadow: 0 0 ${themeConWeb.baseUnit} 0 rgba(0, 0, 0, 0.25);
  cursor: ${({ disabled }) => !disabled && 'pointer'};
  border: ${({ selected }) => selected && `solid 2px ${themeConWeb.color.brandPrimary}`};

  &:hover {
    border: ${({ disabled, selected }) =>
      !disabled && !selected && `solid 1px ${themeConWeb.color.brandPrimary}`};
  }

  & .-creditCardIcon {
    margin-right: calc(${themeConWeb.baseUnit} * 4);
  }

  & .-text {
    flex-grow: 1;
  }

  & .-checkCircle {
    color: ${themeConWeb.color.success};
  }
`

const InputRadioPaymentMethod = styled.input`
  display: none;
`

const RootButtonPaymentMethod = styled.button`
  display: flex;
  align-content: center;
  width: 100%;
  padding: calc(${themeConWeb.baseUnit} * 4);
  border-radius: ${({ active }) => (active ? '4px 4px 0 0' : '4px')};
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  cursor: ${({ disabled }) => !disabled && 'pointer'};

  &:hover {
    border: ${({ active, disabled }) =>
      !disabled && !active && `solid 1px ${themeConWeb.color.brandPrimary}`};
  }
`

const FormContainer = styled(Form)`
  padding: calc(${themeConWeb.baseUnit} * 8);
  background-color: ${themeConWeb.color.grey};
  border-radius: 0 0 ${themeConWeb.baseUnit} ${themeConWeb.baseUnit};
  box-shadow: 0 0 ${themeConWeb.baseUnit} 0 rgba(0, 0, 0, 0.25);
`

export default scriptLoader(HEARTLAND_SCRIPT_URL)(PaymentMethod)
